import React from 'react';
import PropTypes from 'prop-types';
import { propTypes } from 'formsy-react';
import dynamic from 'next/dynamic';
import moment from 'moment';
import { registerLocale } from 'react-datepicker';
import { Alert } from 'reactstrap';
import domainModel from 'domain-model';
import sv from 'date-fns/locale/sv';
import _ from 'lodash';
import qs from 'qs';
import { getAxiosInstance } from '../../utils/appWrapper';
import withFormsyValidations from '../withFormsyValidations';

const { isHoliday } = domainModel.holidays;

registerLocale('sv', sv);

// eslint-disable-next-line
const DatePickerInput = dynamic(() => import('react-datepicker'), {
  ssr: false,
});

const createDayClassNames = (date, deliveries) => {
  if (isHoliday(date)) {
    return undefined;
  }
  if (_.includes(deliveries, moment(date).format('YYYY-MM-DD'))) {
    return 'deliveryOnDate';
  }

  return undefined;
};

class DateTimePicker extends React.Component {
  static get propTypes() {
    return {
      name: PropTypes.string.isRequired,
      demoMode: PropTypes.bool,
      setValue: PropTypes.func.isRequired,
      onChange: PropTypes.func.isRequired,
      ...propTypes,
    };
  }

  constructor(props) {
    super(props);
    this.state = {
      deliveries: [],
      noAvailableDeliveryDays: false,
      demoCurrentDate: undefined, // State to hold the "current" date in demo mode
    };

    this.changeValue = this.changeValue.bind(this);
    this.loadComingOrders = this.loadComingOrders.bind(this);
    this.handleDemoDateChange = this.handleDemoDateChange.bind(this);

    const entity = this.props.entity;
    this.deliveryRulesCalculator =
      new domainModel.deliveryRules.DeliveryRulesCalculator(
        entity.orderFullfillmentCenter,
        entity.customerSubUnit,
        entity.supplierCustomer,
        entity.supplierConnection,
      );
  }

  componentDidMount() {
    this.loadComingOrders();
  }

  changeValue(value) {
    const { name, setValue, onChange } = this.props;
    const fixedValue = moment(value).format('YYYY-MM-DD');
    setValue(fixedValue, false); // this is the performance thief
    onChange(name, fixedValue);
  }

  handleDemoDateChange(date) {
    this.setState({ demoCurrentDate: date });
  }

  async loadComingOrders() {
    const entity = _.get(this.props, 'entity');
    const canMergeOrders = _.get(
      entity,
      'orderFullfillmentCenter.shouldMergeOrders',
      false,
    );
    if (!canMergeOrders) {
      return;
    }
    const customerSubUnitId = _.get(this.props, 'entity.customerSubUnit.id');
    const supplierCustomerId = _.get(this.props, 'entity.supplierCustomer.id');

    const firstPossibleDeliveryDate =
      this.deliveryRulesCalculator.getEarliestPossibleDeliveryDate(
        0,
        this.state.demoCurrentDate,
      );
    if (!firstPossibleDeliveryDate) {
      return;
    }

    const queryStr = qs.stringify({
      range: {
        between: [
          firstPossibleDeliveryDate,
          moment().add(100, 'days').toDate(),
        ],
      },
      customerSubUnitId,
      supplierCustomerId,
      removeId: entity.id,
    });

    const requestUrl = `/reportEngine/reports/customerSubUnit/upcomingCopackOrders?${queryStr}`;
    const response = await getAxiosInstance().get(requestUrl);

    this.setState({
      ...this.state,
      deliveries: response.data.rows.map(x => x.deliveryDate),
    });
  }

  render() {
    const { value, errorMessage, isValid, entity, demoMode, ...extras } =
      this.props;
    const { deliveries, demoCurrentDate } = this.state;

    const date = value ? moment(value).toDate() : undefined;

    const defaultDeliveryBreakpointText =
      'Observera att leverantörens specifika ledtider kan påverka önskat leveransdatum.';
    const deliveryBreakpointText = _.get(
      entity,
      'orderFullfillmentCenter.deliveryBreakpointText',
      defaultDeliveryBreakpointText,
    );

    const firstPossibleDeliveryDate =
      this.deliveryRulesCalculator.getEarliestPossibleDeliveryDate(
        0,
        demoCurrentDate,
      );

    const minDate = entity.overrideFallbackDate
      ? undefined
      : moment(firstPossibleDeliveryDate).toDate();

    const canMergeOrders = _.get(
      entity,
      'orderFullfillmentCenter.shouldMergeOrders',
      false,
    );
    if (!firstPossibleDeliveryDate && !entity.overrideFallbackDate) {
      return (
        <Alert color="danger">
          Det finns inga tillgängliga leveransdatum. Vänligen kontakta{' '}
          {entity.orderFullfillmentCenter.name}
        </Alert>
      );
    }

    return (
      <div>
        {demoMode && (
          <div>
            <label>Set Current Date (Demo Mode):</label>
            <DatePickerInput
              selected={demoCurrentDate}
              onChange={this.handleDemoDateChange}
              dateFormat="yyyy-MM-dd HH:mm"
              showTimeSelect
              timeFormat="HH:mm"
              autoComplete="off"
            />
          </div>
        )}
        <div className="text-muted lh-sm">{deliveryBreakpointText}</div>
        <DatePickerInput
          className={!isValid ? 'border border-danger' : ''}
          selected={date}
          locale="sv"
          showWeekNumbers
          minDate={minDate}
          timeIntervals={15}
          inline
          disabledKeyboardNavigation
          filterDate={inDate =>
            entity.overrideFallbackDate ||
            this.deliveryRulesCalculator.isPossibleToGetDeliveryOnDate(
              inDate,
              demoCurrentDate,
            )
          }
          dayClassName={x => createDayClassNames(x, deliveries)}
          dateFormat="yyyy-MM-dd"
          autoComplete="off"
          {...extras}
          onChange={this.changeValue}
        />

        <div className="text-muted">
          {canMergeOrders && (
            <div>
              <div
                className="deliveryOnDate"
                style={{
                  width: '20px',
                  height: '20px',
                  display: 'inline-block',
                }}
              >
                &nbsp;
              </div>{' '}
              Det finns redan leveranser denna dag
            </div>
          )}
          <div>
            <div
              className="react-datepicker__day--selected"
              style={{ width: '20px', height: '20px', display: 'inline-block' }}
            >
              &nbsp;
            </div>{' '}
            Vald dag
          </div>
        </div>
        <span>{errorMessage}</span>
      </div>
    );
  }
}

export default withFormsyValidations(DateTimePicker);
